import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { companyInfo } from '../eiraa_mock';
import { useToast } from '../hooks/use-toast';
import { contactsAPI } from '../services/api';

const EiraaContact = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    productInterest: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Save to database via API
      await contactsAPI.create({
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        product_interest: formData.productInterest,
        status: 'new'
      });
      
      toast({
        title: "Message Sent!",
        description: "Thank you for your interest in EIRAA. We'll contact you within 24 hours.",
      });
      
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: '',
        productInterest: ''
      });
    } catch (error) {
      console.error('Failed to submit:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Get In Touch</span>
          <h2 className="text-4xl md:text-5xl font-light text-slate-900 mt-3 mb-4">
            Start Your Wellness Journey
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Ready to bring EIRAA into your space? Our team is here to help you choose the perfect sauna for your needs.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <Card className="border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl font-light">Request a Quote</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="border-gray-300 focus:border-amber-600 focus:ring-amber-600"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="border-gray-300 focus:border-amber-600 focus:ring-amber-600"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="border-gray-300 focus:border-amber-600 focus:ring-amber-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="border-gray-300 focus:border-amber-600 focus:ring-amber-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="productInterest">Product Interest</Label>
                  <select
                    id="productInterest"
                    name="productInterest"
                    value={formData.productInterest}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-amber-600 focus:ring-amber-600"
                  >
                    <option value="">Select a product line</option>
                    <option value="EIRAA-DOT">EIRAA-DOT (Economic Variant)</option>
                    <option value="EIRAA-CLEAR">EIRAA-CLEAR (Premium Variant)</option>
                    <option value="Not Sure">Not Sure - Need Consultation</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="border-gray-300 focus:border-amber-600 focus:ring-amber-600"
                    placeholder="Tell us about your requirements..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white text-lg py-6 transition-all duration-300 hover:shadow-lg disabled:opacity-50"
                >
                  {submitting ? 'Sending...' : 'Send Inquiry'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="border-0 shadow-xl bg-gradient-to-br from-amber-600 to-amber-700 text-white">
              <CardHeader>
                <CardTitle className="text-2xl text-white font-light">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="text-white" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1 text-white">Phone</h4>
                    <a href={`tel:${companyInfo.phone}`} className="text-amber-100 hover:text-white transition-colors">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="text-white" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1 text-white">Email</h4>
                    <a href={`mailto:${companyInfo.email}`} className="text-amber-100 hover:text-white transition-colors">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-white" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1 text-white">Location</h4>
                    <p className="text-amber-100">{companyInfo.address}</p>
                    <p className="text-amber-100 text-sm mt-1">Proudly Manufactured in UAE</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="text-white" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1 text-white">Business Hours</h4>
                    <p className="text-amber-100">Monday - Saturday: 8:30 AM - 5:30 PM</p>
                    <p className="text-amber-100">Sunday: Closed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="border-0 shadow-lg bg-white text-center p-6">
                <div className="text-4xl font-bold text-amber-600 mb-2">24-48h</div>
                <div className="text-sm text-gray-600">Response Time</div>
              </Card>
              <Card className="border-0 shadow-lg bg-white text-center p-6">
                <div className="text-4xl font-bold text-amber-600 mb-2">UAE</div>
                <div className="text-sm text-gray-600">Made in UAE</div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EiraaContact;